import { useCallback, useState } from "react";
import { Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { formatEpoch } from "../../lib/format-date";
import {
  triggerExpandSyncRange,
  triggerStripeChargesSync,
} from "../../lib/trigger-stripe-transactions-fetch";
import { StripeConnectInfo } from "../../types/StripeConnectInfo";
import { InlineLoadingIcon } from "../InlineLoadingIcon/InlineLoadingIcon";
import "./SyncStatusBar.scss";

interface SyncStatusBarProps {
  stripeConnectInfo: StripeConnectInfo | null;
  projectId: string | undefined;
}

const SyncStatusBar: React.FC<SyncStatusBarProps> = ({
  stripeConnectInfo,
  projectId,
}) => {
  const [syncing, setSyncing] = useState(false);
  const [expanding, setExpanding] = useState(false);

  const handleExpandRange = useCallback(async () => {
    if (!projectId) {
      return;
    }

    setExpanding(true);
    try {
      const message = await triggerExpandSyncRange(projectId);
      toast.success(message ?? "Range expanded");
    } catch {
      toast.error("Failed to expand range");
    } finally {
      setExpanding(false);
    }
  }, [projectId]);

  const handleSync = useCallback(async () => {
    if (!projectId) {
      return;
    }

    setSyncing(true);
    try {
      const message = await triggerStripeChargesSync(projectId);
      toast.success(message ?? "Sync complete");
    } catch (err) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  }, [projectId]);

  return (
    <div className="sync-status-bar">
      <div className="sync-info">
        {stripeConnectInfo?.lastSyncedAt ? (
          <>
            <span className="sync-stat">
              <span className="sync-stat-label">Total charges</span>
              <strong>{stripeConnectInfo.totalChargesSynced}</strong>
            </span>
            {stripeConnectInfo.syncRangeStart &&
              stripeConnectInfo.syncRangeEnd && (
                <span className="sync-range sync-stat">
                  <span className="sync-stat-label">Date range</span>
                  <div>
                    {formatEpoch(stripeConnectInfo.syncRangeStart)} &ndash;{" "}
                    {formatEpoch(stripeConnectInfo.syncRangeEnd)}
                  </div>
                  <span
                    className="link-styles sync-range-expand-link"
                    onClick={expanding || syncing ? undefined : handleExpandRange}
                    style={{ cursor: expanding || syncing ? "default" : "pointer", opacity: expanding || syncing ? 0.5 : 1 }}
                  >
                    {expanding ? "Expanding..." : "Expand Range"}
                  </span>
                </span>
              )}
            <span className="sync-stat">
              <span className="sync-stat-label">Last synced</span>
              {formatEpoch(stripeConnectInfo.lastSyncedAt)}
            </span>
          </>
        ) : (
          <span className="sync-stat">Not yet synced</span>
        )}
      </div>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={handleSync}
        disabled={syncing || expanding}
      >
        {syncing ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <InlineLoadingIcon /> Syncing...
          </div>
        ) : (
          "Re-sync"
        )}
      </Button>
    </div>
  );
};

export default SyncStatusBar;
